// Generic form component. Renders a list of FieldDef inputs, tracks state
// in a plain object, exposes submit + values up to the caller. Secret
// fields that were already saved render as a `••••••••` placeholder with
// a "Change" button — clicking reveals an empty input (never the stored
// value, since the server never returns it).

import { useState, type FormEvent } from 'react';
import clsx from 'clsx';
import { isFieldVisible, type FieldDef } from './field-defs.js';

export interface ProviderFormProps {
  fields: FieldDef[];
  initialValues: Record<string, unknown>;
  /** True when a secret is already in Secrets Manager — toggles the mask. */
  hasStoredSecret: boolean;
  /** Only admin-tier callers can edit. Passing false renders read-only. */
  editable: boolean;
  submitLabel: string;
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  pending?: boolean;
}

export function ProviderForm(props: ProviderFormProps): JSX.Element {
  const [values, setValues] = useState<Record<string, unknown>>(props.initialValues);
  const [revealedSecrets, setRevealedSecrets] = useState<Set<string>>(new Set());

  const anySecretField = props.fields.some((f) => 'secret' in f && f.secret);
  const canEditSecretDirectly = !(props.hasStoredSecret && anySecretField);

  const setValue = (key: string, v: unknown): void => {
    setValues((prev) => ({ ...prev, [key]: v }));
  };

  const revealSecret = (key: string): void => {
    setRevealedSecrets((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setValue(key, '');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    const toSend: Record<string, unknown> = {};
    for (const f of props.fields) {
      // Hidden fields are NOT submitted — otherwise toggling between
      // modes (e.g. SMTP ↔ SendGrid) would leak stale values from the
      // inactive branch into the saved config.
      if (!isFieldVisible(f, values)) continue;
      const v = values[f.key];
      if (f.kind === 'checkbox') {
        toSend[f.key] = Boolean(v);
      } else if ('secret' in f && f.secret) {
        // Only include secret fields if the user revealed + changed them,
        // OR if no secret exists yet (initial save).
        if (!props.hasStoredSecret || revealedSecrets.has(f.key)) {
          if (v !== '' && v !== undefined && v !== null) toSend[f.key] = v;
        }
      } else {
        toSend[f.key] = v ?? '';
      }
    }
    await props.onSubmit(toSend);
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
      {props.fields
        .filter((f) => isFieldVisible(f, values))
        .map((f) =>
          renderField(f, {
            value: values[f.key],
            setValue: (v) => setValue(f.key, v),
            disabled: !props.editable,
            hasStoredSecret: props.hasStoredSecret && 'secret' in f && Boolean(f.secret),
            revealed:
              revealedSecrets.has(f.key) || !canEditSecretDirectly
                ? revealedSecrets.has(f.key)
                : true,
            onReveal: () => revealSecret(f.key),
          }),
        )}
      {props.editable && (
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={props.pending}
            className={clsx(
              'rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm',
              'hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {props.pending ? 'Saving…' : props.submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}

interface RenderContext {
  value: unknown;
  setValue: (v: unknown) => void;
  disabled: boolean;
  hasStoredSecret: boolean;
  revealed: boolean;
  onReveal: () => void;
}

function renderField(field: FieldDef, ctx: RenderContext): JSX.Element {
  const secret = 'secret' in field && field.secret === true;
  const label = (
    <div className="mb-1 flex items-center justify-between">
      <label htmlFor={field.key} className="text-sm font-medium text-slate-800">
        {field.label}
        {'required' in field && field.required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      {secret && ctx.hasStoredSecret && !ctx.revealed && !ctx.disabled && (
        <button
          type="button"
          onClick={ctx.onReveal}
          className="text-xs font-medium text-brand-600 hover:text-brand-700"
        >
          Change
        </button>
      )}
    </div>
  );

  const help = 'help' in field && field.help && (
    <p className="mt-1 text-xs text-slate-500">{field.help}</p>
  );

  const masked = secret && ctx.hasStoredSecret && !ctx.revealed;

  const baseCls =
    'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500';

  switch (field.kind) {
    case 'text': {
      return (
        <div key={field.key}>
          {label}
          {masked ? (
            <input
              type="text"
              id={field.key}
              value="••••••••"
              disabled
              className={baseCls}
              aria-label={`${field.label} (masked; click Change to replace)`}
            />
          ) : (
            <input
              type={secret ? 'password' : 'text'}
              id={field.key}
              placeholder={field.placeholder}
              value={(ctx.value as string | undefined) ?? ''}
              onChange={(e) => ctx.setValue(e.target.value)}
              disabled={ctx.disabled}
              required={field.required && !ctx.disabled && !ctx.hasStoredSecret}
              className={baseCls}
            />
          )}
          {help}
        </div>
      );
    }
    case 'textarea': {
      return (
        <div key={field.key}>
          {label}
          {masked ? (
            <textarea
              id={field.key}
              rows={3}
              value="••••••••"
              disabled
              className={baseCls}
              aria-label={`${field.label} (masked; click Change to replace)`}
            />
          ) : (
            <textarea
              id={field.key}
              rows={field.rows ?? 4}
              placeholder={field.placeholder}
              value={(ctx.value as string | undefined) ?? ''}
              onChange={(e) => ctx.setValue(e.target.value)}
              disabled={ctx.disabled}
              required={field.required && !ctx.disabled && !ctx.hasStoredSecret}
              className={clsx(baseCls, 'font-mono')}
            />
          )}
          {help}
        </div>
      );
    }
    case 'select': {
      return (
        <div key={field.key}>
          {label}
          <select
            id={field.key}
            value={(ctx.value as string | undefined) ?? ''}
            onChange={(e) => ctx.setValue(e.target.value)}
            disabled={ctx.disabled}
            required={field.required}
            className={baseCls}
          >
            <option value="" disabled>
              Select…
            </option>
            {field.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {help}
        </div>
      );
    }
    case 'checkbox': {
      return (
        <div key={field.key} className="flex items-start gap-2">
          <input
            id={field.key}
            type="checkbox"
            checked={Boolean(ctx.value)}
            onChange={(e) => ctx.setValue(e.target.checked)}
            disabled={ctx.disabled}
            className="mt-0.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          <div>
            <label htmlFor={field.key} className="text-sm font-medium text-slate-800">
              {field.label}
            </label>
            {help}
          </div>
        </div>
      );
    }
  }
}
